import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => [
  { title: "Help & Support — Sandcastle Admin Search" },
  {
    name: "description",
    content:
      "Documentation, how-to guides, and support contact for Sandcastle Admin Search.",
  },
];

const APP_NAME = "Sandcastle Admin Search";
const CONTACT_EMAIL = "support@sandcastle-web.com";

interface Section {
  id: string;
  title: string;
  body: React.ReactNode;
}

const sections: Section[] = [
  {
    id: "install",
    title: "Installing the app",
    body: (
      <>
        <ol>
          <li>
            Open your Shopify admin and visit the Shopify App Store, then
            search for &ldquo;{APP_NAME}&rdquo;.
          </li>
          <li>
            Click <strong>Install</strong> and follow Shopify&rsquo;s prompts to
            approve the access scopes (<code>write_products</code> and{" "}
            <code>read_publications</code>).
          </li>
          <li>
            After install, the app loads inside your Shopify admin under{" "}
            <strong>Apps &rarr; {APP_NAME}</strong>.
          </li>
        </ol>
        <p>
          The first page load may take a few seconds while we wake the app
          server. Subsequent loads are fast.
        </p>
      </>
    ),
  },
  {
    id: "search",
    title: "Searching for products",
    body: (
      <>
        <p>
          The search bar at the top accepts wildcard prefix queries. The
          <strong> Search across</strong> checkboxes underneath let you target
          which fields the search matches against.
        </p>
        <ul>
          <li>
            Check <strong>SKU</strong> only and search <code>BOND</code> to
            find every variant whose SKU starts with BOND.
          </li>
          <li>
            Check <strong>Barcode</strong> and paste a UPC to find a product
            by its physical barcode.
          </li>
          <li>
            Check multiple fields to OR the matches across them.
          </li>
        </ul>
        <p>
          Press Enter or click <strong>Search</strong> to apply the query.
          Click <strong>Clear search</strong> to remove just the text without
          clearing your other filters.
        </p>
      </>
    ),
  },
  {
    id: "filters",
    title: "Filtering",
    body: (
      <>
        <p>
          Click <strong>Add filter</strong> below the search bar to choose
          from any combination of:
        </p>
        <ul>
          <li>Status, vendor, product type, tag, collection, sales channel</li>
          <li>Price range, inventory range, created / updated / published dates</li>
          <li>Gift card, on-sale, published status</li>
          <li>
            Metafield value (pick a definition from the dropdown, then a
            value)
          </li>
          <li>
            <strong>Slow filters</strong> (marked &nbsp;⚠): image presence,
            no-SKU detection, variant count. These require a full-catalog
            scan and show a banner with the scan totals.
          </li>
        </ul>
        <p>
          Filters stack. The applied filter chips appear next to the
          &ldquo;More filters&rdquo; button; click the &times; on any chip to
          remove that single filter, or click <strong>Clear all filters</strong>
          {" "}to remove them all (your search term stays).
        </p>
      </>
    ),
  },
  {
    id: "audits",
    title: "Audit presets",
    body: (
      <>
        <p>
          The chip row at the top (&ldquo;Missing images&rdquo;, &ldquo;No
          SKU&rdquo;, &ldquo;Out of stock&rdquo;, etc.) is a shortcut for
          common catalog audits. Click a preset to apply its filter; click
          again to remove it. Presets are additive &mdash; they stack with
          any other filters you have set.
        </p>
      </>
    ),
  },
  {
    id: "sorting",
    title: "Sorting",
    body: (
      <>
        <p>
          Every column header is sortable. Click once for ascending, again for
          descending.
        </p>
        <p>
          Sorting by columns Shopify&rsquo;s API supports natively (Product,
          Type, Vendor, Inventory, Published, Created, Updated) is instant.
          Sorting by SKU, Status, Price, Compare-at, Tags, Image, or Variants
          triggers a full-catalog scan because those fields can&rsquo;t be
          sorted server-side &mdash; a banner shows the scan progress.
        </p>
      </>
    ),
  },
  {
    id: "columns",
    title: "Display columns",
    body: (
      <>
        <p>
          Click the <strong>Display Columns</strong> dropdown to toggle which
          columns are visible. Your column choice is saved per shop. The
          Product column is always shown.
        </p>
        <p>
          If you apply a filter that uses a hidden column (e.g. filtering by
          price reveals the Price column), the column auto-appears for the
          duration of that filter.
        </p>
      </>
    ),
  },
  {
    id: "saved-views",
    title: "Saved views",
    body: (
      <>
        <p>
          Click <strong>Saved views</strong> above the table to open the
          views menu. To save your current state:
        </p>
        <ol>
          <li>Apply the search, filters, and columns you want to save.</li>
          <li>
            Click <strong>Save current view&hellip;</strong> at the top of the
            menu.
          </li>
          <li>Enter a name (max 50 characters).</li>
          <li>
            The view captures your search query, all filter values, sort
            order, page size, and visible columns.
          </li>
        </ol>
        <p>
          Click any saved view to restore that state in one click. Each shop
          can have up to 50 saved views.
        </p>
      </>
    ),
  },
  {
    id: "bulk-edit",
    title: "Bulk editing",
    body: (
      <>
        <p>
          Select one or more product rows (use the checkboxes on the left, or
          the header checkbox to select all rows on the page). A bulk-action
          bar appears with two main buttons:
        </p>
        <ul>
          <li>
            <strong>Quick edit (N)</strong> &mdash; inline modal for status,
            vendor, product type, add tags, remove tags. Fast.
          </li>
          <li>
            <strong>Open in Shopify editor (N)</strong> &mdash; hands the
            selected products off to Shopify&rsquo;s native bulk editor
            (more fields, spreadsheet UI). When you click back, you return
            to {APP_NAME} at the same view.
          </li>
        </ul>
        <p>
          The <strong>More actions</strong> menu (down arrow) has additional
          Shopify-native flows that open in a new browser tab: unlist,
          delete, sales channels in/out, catalogs in/out, collections
          add/remove.
        </p>
      </>
    ),
  },
  {
    id: "duplicates",
    title: "Finding duplicates",
    body: (
      <>
        <p>
          Click <strong>Duplicate finder</strong> in the left nav. The app
          scans your catalog (up to 2,000 products by default; you can bump
          to 5,000 or 10,000) and groups products that share a SKU, barcode,
          or title.
        </p>
        <p>
          Each group shows the colliding products with deep links to their
          Shopify admin pages so you can resolve the duplicate.
        </p>
      </>
    ),
  },
  {
    id: "export",
    title: "CSV export",
    body: (
      <>
        <p>
          Click <strong>Export CSV</strong> to download the products matching
          your current filters and sort order, with whatever columns you have
          visible.
        </p>
        <p>
          The export is capped at 5,000 products per request to keep response
          times reasonable. To export more, narrow your filter first or split
          across multiple exports.
        </p>
      </>
    ),
  },
  {
    id: "page-size",
    title: "Changing the page size",
    body: (
      <>
        <p>
          The <strong>Rows per page</strong> dropdown at the bottom-left of
          the table lets you switch between 10, 25, 50, 100, and 250 rows.
          Your choice is saved per shop and persists across sessions.
        </p>
      </>
    ),
  },
  {
    id: "uninstall",
    title: "Uninstalling",
    body: (
      <>
        <p>
          From your Shopify admin: <strong>Apps &rarr; {APP_NAME} &rarr;
          remove</strong>. Shopify notifies us via the{" "}
          <code>app/uninstalled</code> webhook and we immediately delete your
          session record. 48 hours later, the{" "}
          <code>shop/redact</code> webhook from Shopify triggers full deletion
          of any remaining data (saved views and preferences).
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Need more help?",
    body: (
      <>
        <p>
          Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          Please include:
        </p>
        <ul>
          <li>Your shop domain (e.g. <code>your-store.myshopify.com</code>)</li>
          <li>What you were trying to do</li>
          <li>What happened instead (a screenshot is ideal)</li>
        </ul>
        <p>
          We read every message and aim to respond within one business day.
        </p>
      </>
    ),
  },
];

export default function Support() {
  return (
    <main style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Help &amp; support</h1>
          <p style={metaStyle}>
            Documentation, walkthroughs, and contact for {APP_NAME}.
          </p>
        </header>

        <nav style={tocStyle} aria-label="Table of contents">
          <strong style={tocTitleStyle}>On this page</strong>
          <ul style={tocListStyle}>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        {sections.map((s) => (
          <section key={s.id} id={s.id} style={sectionStyle}>
            <h2 style={h2Style}>{s.title}</h2>
            <div>{s.body}</div>
          </section>
        ))}

        <footer style={footerStyle}>
          <p>
            See also: <a href="/faq">FAQ</a> &middot;{" "}
            <a href="/changelog">What&rsquo;s new</a> &middot;{" "}
            <a href="/privacy">Privacy policy</a> &middot;{" "}
            <a href="/terms">Terms of service</a>
          </p>
        </footer>
      </article>
    </main>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f6f6f7",
  padding: "48px 24px",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#202223",
  lineHeight: 1.6,
};

const articleStyle: React.CSSProperties = {
  maxWidth: 760,
  margin: "0 auto",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  padding: "48px 56px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const h1Style: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  margin: "0 0 8px",
};

const metaStyle: React.CSSProperties = {
  color: "#6d7175",
  fontSize: 14,
  margin: "0 0 24px",
};

const tocStyle: React.CSSProperties = {
  backgroundColor: "#f6f6f7",
  borderRadius: 8,
  padding: "16px 20px",
  marginBottom: 32,
};

const tocTitleStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "#6d7175",
  marginBottom: 8,
};

const tocListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: "none",
  columns: 2,
  columnGap: 24,
};

const sectionStyle: React.CSSProperties = {
  marginTop: 32,
  scrollMarginTop: 24,
};

const h2Style: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  margin: "0 0 12px",
};

const footerStyle: React.CSSProperties = {
  marginTop: 48,
  paddingTop: 24,
  borderTop: "1px solid #e1e3e5",
  color: "#6d7175",
  fontSize: 14,
};
